import { Children, forwardRef, PropsWithChildren, useImperativeHandle, useRef } from 'react'
import { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Styles from './Carousel.module.scss'
import { ErrorBoundary } from '@sentry/react'
import { NextButton } from './NextButton'
import { PreviousButton } from './PreviousButton'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import * as CSS from 'csstype'
import { UseEmblaCarouselType } from 'embla-carousel-react/components/useEmblaCarousel'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'

interface GradientProps {
  withGradient?: boolean
  gradientOffset?: number
  gradientColor?: string
}

interface Props extends GradientProps {
  options?: EmblaOptionsType
  offset?: number
  paddingLeft?: string
  paddingRight?: string
  withControls?: boolean
  noGap?: boolean
  buttonSize?: 'xs' | 'sm' | 'md'
  fullWidth?: boolean
  onSlideScrolled?: (event: EmblaCarouselType) => void
  slideGap?: number
  plugins?: EmblaPluginType[]
}

export interface WithEmbla {
  embla: UseEmblaCarouselType
}

export const DEFAULT_OPTIONS: EmblaOptionsType = {
  loop: false,
  axis: 'x',
  slidesToScroll: 1,
  align: 'start',
  skipSnaps: true,
}

export const InnerCarousel = ({
  children,
  offset = undefined,
  paddingLeft = undefined,
  paddingRight = undefined,
  noGap = false,
  withControls = true,
  buttonSize = 'md',
  fullWidth = false,
  onSlideScrolled,
  withGradient = false,
  gradientOffset = undefined,
  gradientColor = undefined,
  slideGap = undefined,
  embla,
}: PropsWithChildren<Omit<Props, 'options'> & WithEmbla>) => {
  const [emblaRef, emblaApi] = embla
  const { isMobile } = useResponsiveDesign()
  const arrayChildren = Children.toArray(children)
  const rightScrollButtonRef = useRef<HTMLDivElement>(null)
  const isScrolledToRight = useIntersectionObserver(rightScrollButtonRef, {
    threshold: 0.1,
  })?.isIntersecting

  emblaApi?.on('select', (event) => {
    onSlideScrolled?.(event)
  })

  const style: CSS.Properties = {
    ['--offset' as any]: offset ? `${offset}px` : undefined,
    ['--gradient-offset' as any]: gradientOffset ? `-${gradientOffset}px` : undefined,
    ['--gradient-color' as any]: gradientColor || 'var(--default-bg)',
    ['--slide-gap' as any]: slideGap ? `${slideGap}px` : undefined,
  }

  return (
    <section className={Styles.embla} style={style}>
      <div
        className={cx(Styles.emblaViewport, {
          [Styles.withGradient]: withGradient,
          [Styles.isScrolledToRight]: isScrolledToRight,
        })}
        ref={emblaRef}
      >
        <div className={cx(Styles.emblaContainer, { [Styles.noGap]: noGap })}>
          {arrayChildren.map((child, index) => (
            <div
              className={cx(Styles.emblaSlide, {
                [Styles.offsetLeft]: offset && index === 0,
                [Styles.offsetRight]: offset && index === arrayChildren.length - 1,
                [Styles.fullWidth]: fullWidth,
              })}
              style={{
                paddingLeft: index === 0 ? paddingLeft : undefined,
                paddingRight: index === arrayChildren.length - 1 ? paddingRight : undefined,
              }}
              key={index}
            >
              {child}
            </div>
          ))}
          {withGradient && <div ref={rightScrollButtonRef} data-testid="right-scroll-button" />}
        </div>
      </div>
      {emblaApi && withControls && !isMobile && <PreviousButton emblaApi={emblaApi} size={buttonSize} />}
      {emblaApi && withControls && !isMobile && <NextButton emblaApi={emblaApi} size={buttonSize} />}
    </section>
  )
}

const CarouselContainer = forwardRef<EmblaCarouselType, PropsWithChildren<Props>>(({ children, ...props }, ref) => {
  const { plugins, options } = props
  const embla = useEmblaCarousel({ ...DEFAULT_OPTIONS, ...options }, plugins)
  const [, emblaApi] = embla

  useImperativeHandle(ref, () => emblaApi as EmblaCarouselType, [emblaApi])

  return (
    <InnerCarousel embla={embla} {...props}>
      {children}
    </InnerCarousel>
  )
})

const WithErrorBoundary = forwardRef<EmblaCarouselType, PropsWithChildren<Props>>((props, ref) => (
  <ErrorBoundary>
    <CarouselContainer ref={ref} {...props} />
  </ErrorBoundary>
))

export { WithErrorBoundary as Carousel }
